import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	InputLabel,
	TextField,
} from '@mui/material'
import * as React from 'react'
import CloseIcon from '@mui/icons-material/Close'

import PlaylistAddCheckCircleIcon from '@mui/icons-material/PlaylistAddCheckCircle'
import { ShiftEndTaskSummary } from '../../../../types/Reports/ShiftEnd/ShiftEndWorkItems'
import { updateWorkItemProgress } from '../../../../services/workItemService'
import { showErrorToast } from '../../../../services/CommonServices/alertServices'
import { useTokenClaims } from '../../../../security/useTokenClaims'
import { useParams } from 'react-router-dom'
import { useIndexedDB } from '../../../hooks/useIndexedDB'
import { BuildSheetDialog } from './BuildSheetDialog'
import { useShiftEndContext } from '../ShiftEndContext'
type ProgressDialogProps = {
	task: ShiftEndTaskSummary
	uom: string
	workItemId: number
	loadData: () => void
	buildSheetRequired: boolean
}

export const ProgressDialog = ({
	task,
	uom,
	workItemId,
	loadData,
	buildSheetRequired,
}: ProgressDialogProps) => {
	const [open, setOpen] = React.useState(false)
	const [progressValue, setProgressValue] = React.useState<number>()
	const [submitting, setSubmitting] = React.useState<boolean>(false)
	const [buildSheetOpen, setBuildSheetOpen] = React.useState(false)
	const { id, role } = useTokenClaims()
	const { shiftDate } = useShiftEndContext()
	const params = useParams()
	const db = useIndexedDB()
	React.useEffect(() => {
		return () => setProgressValue(0)
	}, [])

	const handleClickOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		setOpen(true)
	}
	const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		setOpen(false)
	}

	function isValid() {
		return progressValue && progressValue <= task.maxQuantity
	}

	async function handleSubmit(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		event.stopPropagation()
		if (isValid()) {
			// Check if work is complete and build sheet is required
			if (progressValue === task.maxQuantity && buildSheetRequired) {
				// Open build sheet dialog first, don't submit progress yet
				setOpen(false)
				setBuildSheetOpen(true)
			} else {
				// Submit progress normally for partial completion
				await submitProgress()
			}
		}
	}

	const submitProgress = async () => {
		const foremanId = role.title === 'Foreman' ? id : parseInt(params.id!)
		setSubmitting(true)
		try {
			await updateWorkItemProgress(workItemId, task.id, progressValue!, shiftDate, foremanId, false, db)
			setOpen(false)
			loadData()
		} catch (ex) {
			showErrorToast({
				message: ex instanceof Error ? ex.message : 'An error occurred while updating progress',
			})
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<>
			<Button
				onClick={handleClickOpen}
				title='Progress'
				color='primary'
				variant='contained'
				size='small'
			>
				<PlaylistAddCheckCircleIcon /> Progress
			</Button>

			<Dialog open={open} onClose={handleClose} fullWidth>
				<div
					onClick={(e: React.MouseEvent<HTMLDivElement>) => {
						e.stopPropagation() // Prevent any event inside the dialog from bubbling up
					}}
				>
					<DialogTitle>
						Progress {task.title} ({task.maxQuantity} {uom} Remaining)
					</DialogTitle>
					<IconButton
						onClick={handleClose}
						sx={(theme) => ({
							position: 'absolute',
							right: 8,
							top: 8,
							color: theme.palette.grey[500],
						})}
					>
						<CloseIcon />
					</IconButton>
					<DialogContent>
						<Box sx={{ display: 'flex', flexDirection: 'column' }} gap={4}>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} gap={2}>
								<InputLabel htmlFor='progress-text'>{task.quantityComplete} + </InputLabel>
								<TextField
									sx={{ backgroundColor: !isValid() ? 'rgba(255,230,222,1)' : 'inherit' }}
									type='number'
									id='progress-text'
									size='small'
									value={progressValue}
									onChange={(e) => setProgressValue(parseFloat(e.target.value))}
									inputProps={{
										min: 0,
									}}
									InputProps={{
										startAdornment: <InputAdornment position='start'>{uom}</InputAdornment>,
									}}
								/>
								<Button
									variant='contained'
									size='small'
									onClick={() => setProgressValue(task.maxQuantity)}
									startIcon={<PlaylistAddCheckCircleIcon />}
								>
									Claim Remaining
								</Button>
							</Box>
						</Box>
					</DialogContent>
					<DialogActions sx={{ justifyContent: 'space-between' }}>
						<Button color='warning' variant='contained' onClick={handleClose}>
							Cancel
						</Button>
						{submitting ? (
							<CircularProgress color='success' />
						) : (
							<Button color='success' variant='contained' onClick={handleSubmit}>
								Submit
							</Button>
						)}
					</DialogActions>
				</div>
			</Dialog>

			{/* Build Sheet Dialog */}
			<BuildSheetDialog
				open={buildSheetOpen}
				onClose={() => {
					setBuildSheetOpen(false)
					setOpen(true)
				}}
				task={task}
				workItemTaskId={task.id}
				postSubmit={submitProgress}
			/>
		</>
	)
}
